import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProducerUsage {
  products_used: number;
  products_limit: number | null;
  events_used: number;
  events_limit: number | null;
  accommodations_used: number;
  accommodations_limit: number | null;
  flea_markets_used: number;
  flea_markets_limit: number | null;
  team_members_used: number;
  team_members_limit: number | null;
}

/** Raw row returned by the get_producer_feature_usage RPC. */
interface FeatureUsageRow {
  feature_key: string;
  used: number;
  limit_value: number | null;
  remaining: number | null;
  is_limited: boolean;
  effective_enabled: boolean;
}

/**
 * Fetches live usage counts + plan limits for a producer.
 *
 * Source: canonical RPC get_producer_feature_usage (see docs/SoT/producer-subscriptions-and-usage-limits.md).
 * Counting rules and limit resolution are fully server-side — this hook is a thin wrapper.
 *
 * NOTE: DB enforcement (insert triggers) counting alignment is a separate follow-up step.
 * This hook is the canonical DISPLAY source; it may temporarily differ from enforcement counts.
 */
export function useProducerUsage(producerId: string | undefined) {
  const query = useQuery({
    queryKey: ['producer-feature-usage', producerId],
    queryFn: async (): Promise<ProducerUsage> => {
      if (!producerId) {
        return {
          products_used: 0,
          products_limit: null,
          events_used: 0,
          events_limit: null,
          accommodations_used: 0,
          accommodations_limit: null,
          flea_markets_used: 0,
          flea_markets_limit: null,
          team_members_used: 0,
          team_members_limit: null,
        };
      }

      // RPC not yet in generated types — cast required until types are regenerated.
      const { data, error } = await (supabase as any).rpc('get_producer_feature_usage', {
        p_producer_id: producerId,
      });

      if (error) {
        console.error('Failed to fetch producer feature usage:', error);
        throw error;
      }

      const rows: FeatureUsageRow[] = data ?? [];
      const byKey = Object.fromEntries(rows.map((r) => [r.feature_key, r]));

      return {
        products_used: byKey['products']?.used ?? 0,
        products_limit: byKey['products']?.limit_value ?? null,
        events_used: byKey['events']?.used ?? 0,
        events_limit: byKey['events']?.limit_value ?? null,
        accommodations_used: byKey['accommodations']?.used ?? 0,
        accommodations_limit: byKey['accommodations']?.limit_value ?? null,
        flea_markets_used: byKey['flea_markets']?.used ?? 0,
        flea_markets_limit: byKey['flea_markets']?.limit_value ?? null,
        team_members_used: byKey['team_members']?.used ?? 0,
        team_members_limit: byKey['team_members']?.limit_value ?? null,
      };
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Format usage as "X / Y" or "X / Obegränsat" */
export function formatUsage(used: number, limit: number | null): string {
  return `${used} / ${limit === null ? 'Obegränsat' : limit}`;
}

/** Returns a soft warning level based on usage ratio */
export function getUsageLevel(used: number, limit: number | null): 'ok' | 'near' | 'at' {
  if (limit === null) return 'ok';
  if (used >= limit) return 'at';
  if (used >= limit * 0.8) return 'near';
  return 'ok';
}
